<template>
  <div>
    <div
      v-show="opened"
      class="modal-backdrop"
      @click="setOpened(false)"
    />

    <BInputGroup
      :class="{ 'exposed' : opened }"
      :disabled="disabled"
      class="h-100"
    >
      <BInputGroupText
        class="h-100"
        is-text
      >
        <b-icon
          icon="search"
          class="fw-bold text-black"
        />
      </BInputGroupText>
      <BFormInput
        ref="input"
        v-model="inputValue"
        data-test-id="search-customer-input"
        name="keyword"
        :disabled="disabled"
        :placeholder="placeholder"
        class="blank-input h-100"
        @focus="handleFocus"
        @input="emitSearch"
        @keydown.enter="emitSubmit"
      />
      <BInputGroupText
        v-if="inputValue"
        class="h-100 me-1"
        is-text
        @click="clearSearch"
      >
        <b-icon
          icon="times-circle"
          class="fw-bold text-black"
        />
      </BInputGroupText>
    </BInputGroup>
  </div>
</template>

<script setup lang="ts">
import {ref, watch} from 'vue'

const props = withDefaults(defineProps<{
  disabled?: boolean
  searchLabel: string
  placeholder?: string
  value?: string
  emptyLabel: string
  initialKeyword?: string
  submitOnClose?: boolean
}>(), {
  disabled: false,
  initialKeyword: '',
  submitOnClose: false
})

const emit = defineEmits<{(e: 'change', value: boolean): void
  (e: 'focus', value: boolean): void
  (e: 'search', value: string): void
  (e: 'submit', value: string): void
  (e: 'update', value: string): void
}>()

const input = ref<any>(null)
const opened = ref(false)
const keyword = ref(props.initialKeyword)
const inputValue = ref('')

watch(() => props.initialKeyword, (n) => {
  if (typeof n === 'string') keyword.value = n
})

watch(() => props.value, () => {
  if (props.value !== inputValue.value) {
    inputValue.value = props.value || ''
  }
}, {immediate: true})

function setOpened(state: boolean, submitted = false): void {
  opened.value = state
  if (!state) {
    blurInput()
    if (props.submitOnClose && !submitted && keyword.value !== props.initialKeyword) emitSubmit()
  }
  emit('change', state)
}

function handleFocus(): void {
  emit('focus', true)
}

function emitSearch(): void {
  emit('search', inputValue.value)
}

function emitSubmit(): void {
  if (opened.value) setOpened(false, true)
  emit('submit', keyword.value)
}

function clearSearch(): void {
  inputValue.value = ''
  emit('search', '')
}

function blurInput(): void {
  const inputRef = input.value?.$el ? input.value.$el : input.value
  if (typeof inputRef?.blur === 'function') {
    inputRef.blur()
    return
  }
  inputRef?.querySelector?.('input')?.blur?.()
}
</script>

<style scoped lang="scss">
@import '../../../scss/colors';

.modal-backdrop {
  opacity: .6;
}

input,
.input-group-text {
  border: 0 !important;
  background-color: $gray-100 !important;
  text-overflow: ellipsis;
}

.input-icon {
  padding: 0.675rem;
  background-color: $white !important;
}

.blank-input {
  min-height: 2.5rem;
}

.blank-input {
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
      transition: background-color 5000s ease-in-out 0s;
  }
}

.faultcode_input .blank-input {
  background-color: $white !important;
}

.faultcode_input .input-group-text {
  background-color: $white !important;
}

.faultcode_input .border {
  border: 1px solid $gray-700 !important;
}

.faultcode_input {
  max-width: 48rem !important;
}
</style>
